import React, { useState } from 'react';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Ban, Bell, Calendar, Check, CheckCircle2, ChevronDown, ChevronUp, CircleUserRound, ClipboardList, Copy, Cpu, Eye, FileStack, GitBranch, Globe2, History, HousePlug, Info, LayoutDashboard, Leaf, Link, Loader2, Mail, MessageSquare, MoreVertical, Paperclip, Pencil, Plus, RefreshCw, RotateCw, Save, Search, Send, Settings, Share2, Shield, Tag, ThermometerSun, Trash2, User, X, XCircle, Zap } from 'lucide-react';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, 
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell 
} from 'recharts';
import { AgGridReact } from 'ag-grid-react';
import {
  AllEnterpriseModule,
  colorSchemeLightCold,

  themeQuartz,
} from 'ag-grid-enterprise';
import type { ColDef } from 'ag-grid-community';

const enterpriseModules = [AllEnterpriseModule];

const agTheme = themeQuartz.withPart(colorSchemeLightCold).withParams({
  fontFamily: 'Inter, system-ui, sans-serif',
  fontSize: 13,
  rowHeight: 44,
  headerHeight: 44,
  borderRadius: 8,
});

const dummyRowData = [
  { id: 1, name: 'Alfa Santrali', status: 'Aktif', limit: '15 MW' },
  { id: 2, name: 'Beta Santrali', status: 'Pasif', limit: '20 MW' },
  { id: 3, name: 'Gama Santrali', status: 'Uyarı', limit: '12 MW' },
  { id: 4, name: 'Delta Santrali', status: 'Bakımda', limit: '30 MW' },
];

const dummyColDefs: ColDef[] = [
  { field: 'id', headerName: 'ID', width: 70 },
  { field: 'name', headerName: 'Tesis Adı', flex: 1 },
  { field: 'status', headerName: 'Durum', width: 130 },
  { field: 'limit', headerName: 'Güç Sınırı', width: 140 },
];

type ButtonVariant = {
  id: string;
  label: string;
  icon: React.ElementType;
  className: string;
};

const variants: ButtonVariant[] = [
  { id: 'add', label: 'Ekle', icon: Plus, className: 'bg-blue-600 text-white hover:bg-blue-700' },
  { id: 'comment', label: 'Gönder', icon: Send, className: 'bg-cyan-600 text-white hover:bg-cyan-700' },
  { id: 'edit', label: 'Güncelle', icon: Pencil, className: 'bg-amber-500 text-white hover:bg-amber-600' },
  { id: 'save', label: 'Kaydet', icon: Save, className: 'bg-emerald-600 text-white hover:bg-emerald-700' },
  { id: 'copy', label: 'Kopyala', icon: Copy, className: 'bg-indigo-600 text-white hover:bg-indigo-700' },
  { id: 'send', label: 'Gönder', icon: Mail, className: 'bg-teal-600 text-white hover:bg-teal-700' },
  { id: 'share', label: 'Paylaş', icon: Share2, className: 'bg-fuchsia-600 text-white hover:bg-fuchsia-700' },
  { id: 'link', label: 'İlişkilendir', icon: Link, className: 'bg-slate-700 text-white hover:bg-slate-800' },
  { id: 'delete', label: 'Sil', icon: Trash2, className: 'bg-rose-600 text-white hover:bg-rose-700' },
  { id: 'yes', label: 'Evet', icon: Check, className: 'bg-emerald-600 text-white hover:bg-emerald-700' },
  { id: 'no', label: 'Hayır', icon: X, className: 'bg-rose-600 text-white hover:bg-rose-700' },
  { id: 'ok', label: 'Tamam', icon: CheckCircle2, className: 'bg-blue-600 text-white hover:bg-blue-700' },
  { id: 'cancel', label: 'İptal', icon: Ban, className: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
  { id: 'abort', label: 'Vazgeç', icon: Ban, className: 'bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-inset ring-gray-300' },
  { id: 'close', label: 'Kapat', icon: X, className: 'bg-gray-100 text-gray-700 hover:bg-gray-200' },
];

const actionIcons = [
  { id: 'i-add', label: 'Ekle', icon: Plus },
  { id: 'i-edit', label: 'Güncelle', icon: Pencil },
  { id: 'i-save', label: 'Kaydet', icon: Save },
  { id: 'i-delete', label: 'Sil', icon: Trash2 },
  { id: 'i-share', label: 'Paylaş', icon: Share2 },
  { id: 'i-link', label: 'İlişkilendir', icon: Link },
  { id: 'i-close', label: 'Kapat', icon: X },
  { id: 'i-comment', label: 'Yorum (Gönder)', icon: MessageSquare },
  { id: 'i-history', label: 'Geçmiş', icon: History },
  { id: 'i-attachment', label: 'Ek', icon: Paperclip },
  { id: 'i-preview', label: 'Önizleme', icon: Eye },
  { id: 'i-more', label: 'Daha Fazla', icon: MoreVertical },
];

function UISection({ title, isOpen, onToggle, children }: { title: string; isOpen: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden flex-shrink-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between bg-gray-50/50 p-4 text-left transition hover:bg-gray-50"
      >
        <h2 className="text-lg font-medium text-gray-800">{title}</h2>
        {isOpen ? <ChevronUp size={20} className="text-gray-400" /> : <ChevronDown size={20} className="text-gray-400" />}
      </button>

      {isOpen && (
        <div className="p-6 border-t border-gray-100 bg-white">
          {children}
        </div>
      )}
    </section>
  );
}

function MultiSelectDemo() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(['opt1', 'opt3']);
  const options = [
    { id: 'opt1', label: 'Bakım Gerekli' },
    { id: 'opt2', label: 'Verimi Düşük' },
    { id: 'opt3', label: 'Kritik Uyarı' },
    { id: 'opt4', label: 'İnceleniyor' },
  ];

  const handleToggle = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <div className="flex flex-col gap-1.5 md:col-span-2">
      <label className="text-sm font-medium text-gray-700">Etiketler (Çoklu Seçim - Checkbox)</label>
      <div className="relative group">
        <Tag size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors pointer-events-none" />
        <button 
          onClick={() => setOpen(!open)}
          className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm text-gray-700 outline-none transition hover:bg-gray-50 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        >
          <span className="truncate">
            {selected.length > 0 
              ? options.filter(o => selected.includes(o.id)).map(o => o.label).join(', ')
              : 'Seçiniz...'}
          </span>
          {open ? <ChevronUp size={16} className="text-gray-500 flex-shrink-0 ml-2" /> : <ChevronDown size={16} className="text-gray-500 flex-shrink-0 ml-2" />}
        </button>

        {open && (
          <div className="absolute left-0 top-full mt-1.5 w-full rounded-lg border border-gray-100 bg-white shadow-xl ring-1 ring-black/5 z-20 max-h-48 overflow-y-auto py-1 animate-in fade-in slide-in-from-top-1">
            {options.map(opt => (
              <label key={opt.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50 transition group">
                <input 
                  type="checkbox" 
                  checked={selected.includes(opt.id)}
                  onChange={() => handleToggle(opt.id)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition group-hover:border-blue-500"
                />
                <span className="text-sm text-gray-700 group-hover:text-gray-900">{opt.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MultiSelectChipsDemo() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(['zone1']);
  const options = [
    { id: 'zone1', label: 'Ege Bölgesi' },
    { id: 'zone2', label: 'Marmara Bölgesi' },
    { id: 'zone3', label: 'İç Anadolu Bölgesi' },
    { id: 'zone4', label: 'Akdeniz Bölgesi' },
  ];

  const handleToggle = (id: string) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectedOptions = options.filter(o => selected.includes(o.id));

  return (
    <div className="flex flex-col gap-3 md:col-span-2">
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">Görev Bölgeleri (Chip Gösterimli Çoklu Seçim)</label>

        {/* Selected Chips Area */}
        {selectedOptions.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-1">
            {selectedOptions.map(opt => (
              <span key={opt.id} className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 ring-1 ring-inset ring-blue-700/10 transition-all">
                {opt.label}
                <button 
                  type="button" 
                  onClick={() => handleToggle(opt.id)} 
                  title="Kaldır"
                  className="rounded-full p-0.5 hover:bg-blue-200 focus:outline-none focus:ring-1 focus:ring-blue-500 transition"
                >
                  <X size={14} className="text-blue-600" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="relative mt-1 group">
          <Tag size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors pointer-events-none" />
          <button 
            onClick={() => setOpen(!open)}
            className="flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm text-gray-400 outline-none transition hover:bg-gray-50 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            <span>Daha fazla bölge seç...</span>
            {open ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0 ml-2" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0 ml-2" />}
          </button>

          {open && (
            <div className="absolute left-0 top-full mt-1.5 w-full rounded-lg border border-gray-100 bg-white shadow-xl ring-1 ring-black/5 z-20 max-h-48 overflow-y-auto py-1 animate-in fade-in slide-in-from-top-1">
              {options.map(opt => (
                <label key={opt.id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50 transition group">
                  <input 
                    type="checkbox" 
                    checked={selected.includes(opt.id)}
                    onChange={() => handleToggle(opt.id)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition group-hover:border-blue-500"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900">{opt.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function UIPage() {
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    buttons: true,
    icons: false,
    badges: false,
    toasts: false,
    tables: false,
    modals: false,
    tabs: false,
    forms: false,
    panels: false,
    tags: false,
    charts: false,
    loading: false,
    stats: false,
    uploads: false,
    agileCards: false,
    dateRange: false,
  });

  const [activeTab, setActiveTab] = useState<string>('comments');
  const [activeGlobalTab, setActiveGlobalTab] = useState<string>('dashboard');
  const [selectedTags, setSelectedTags] = useState<number[]>([1, 3]);

  const toggleTag = (id: number) => {
    setSelectedTags(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSection = (key: string) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const expandAll = () => setOpenSections({ buttons: true, icons: true, badges: true, toasts: true, tables: true, modals: true, tabs: true, forms: true, panels: true, tags: true, charts: true, loading: true, stats: true, uploads: true, agileCards: true, dateRange: true });
  const collapseAll = () => setOpenSections({ buttons: false, icons: false, badges: false, toasts: false, tables: false, modals: false, tabs: false, forms: false, panels: false, tags: false, charts: false, loading: false, stats: false, uploads: false, agileCards: false, dateRange: false });

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // CMD+K (Mac) or CTRL+K (Windows)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className="flex h-full flex-col gap-6 overflow-y-auto w-full pb-10">
      <div className="flex items-center justify-between shrink-0 flex-wrap gap-4">
        <h1 className="text-xl font-semibold text-gray-800">UI Bileşenleri</h1>

        {/* Arama Alanı */}
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input 
            ref={searchInputRef}
            type="text" 
            placeholder="Bileşenlerde ara... (Yakında)" 
            className="w-full sm:w-64 md:w-80 rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-14 text-sm text-gray-700 transition placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <kbd className="inline-flex rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[10px] sm:text-xs font-semibold text-gray-500">
              ⌘K
            </kbd>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end px-1">
        <div className="flex items-center gap-3">
          <button onClick={expandAll} className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition">Hepsini Aç</button>
          <div className="w-1 h-1 rounded-full bg-gray-300"></div>
          <button onClick={collapseAll} className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition">Hepsini Kapat</button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto flex flex-col gap-8 pb-20">

        {/* Butonlar Bölümü */}
        <UISection title="Butonlar (Buttons)" isOpen={openSections.buttons} onToggle={() => toggleSection('buttons')}>
          <div className="flex flex-col gap-10">
            {/* Primary Action Buttons */}
            <div className="flex flex-col gap-4">
              <h3 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Ana Aksiyon Butonları</h3>
            </div>
            {/* Normal Boyut */}
            <div>
              <h3 className="mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wider">Normal Boyut</h3>
              <div className="flex flex-col gap-6">

                {/* Aktif Hal */}
                <div className="flex flex-start flex-col xl:flex-row gap-6">
                  <div className="flex flex-wrap items-center gap-3 flex-1">
                    {variants.map((v) => (
                      <button key={v.id} className={`flex w-32 items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${v.className}`}>
                        <v.icon size={16} />
                        {v.label}
                      </button>
                    ))}
                  </div>
                  <div className="hidden xl:block h-32 w-px bg-gray-200 shrink-0"></div>
                  <div className="flex flex-wrap items-center gap-3 xl:max-w-[200px]">
                    {variants.map((v) => (
                      <button key={`icon-${v.id}`} className={`flex items-center justify-center rounded-lg p-2 transition disabled:cursor-not-allowed disabled:opacity-50 ${v.className}`}>
                        <v.icon size={16} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="h-px bg-gray-50 my-2"></div>

                {/* Devre Dışı Hal */}
                <div className="flex flex-start flex-col xl:flex-row gap-6">
                  <div className="flex flex-wrap items-center gap-3 flex-1">
                    {variants.map((v) => (
                      <button disabled key={v.id} className={`flex w-32 items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${v.className}`}>
                        <v.icon size={16} />
                        {v.label}
                      </button>
                    ))}
                  </div>
                  <div className="hidden xl:block h-32 w-px bg-gray-200 shrink-0"></div>
                  <div className="flex flex-wrap items-center gap-3 xl:max-w-[200px]">
                    {variants.map((v) => (
                      <button disabled key={`icon-${v.id}`} className={`flex items-center justify-center rounded-lg p-2 transition disabled:cursor-not-allowed disabled:opacity-50 ${v.className}`}>
                        <v.icon size={16} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <hr className="border-gray-100" />

            {/* Küçük Boyut */}
            <div>
              <h3 className="mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wider">Küçük Boyut</h3>
              <div className="flex flex-col gap-6">

                {/* Aktif Hal */}
                <div className="flex flex-start flex-col xl:flex-row gap-6">
                  <div className="flex flex-wrap items-center gap-3 flex-1">
                    {variants.map((v) => (
                      <button key={v.id} className={`flex w-28 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${v.className}`}>
                        <v.icon size={14} />
                        {v.label}
                      </button>
                    ))}
                  </div>
                  <div className="hidden xl:block h-28 w-px bg-gray-200 shrink-0"></div>
                  <div className="flex flex-wrap items-center gap-3 xl:max-w-[200px]">
                    {variants.map((v) => (
                      <button key={`icon-${v.id}`} className={`flex items-center justify-center rounded-md p-1.5 transition disabled:cursor-not-allowed disabled:opacity-50 ${v.className}`}>
                        <v.icon size={14} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="h-px bg-gray-50 my-2"></div>

                {/* Devre Dışı Hal */}
                <div className="flex flex-start flex-col xl:flex-row gap-6">
                  <div className="flex flex-wrap items-center gap-3 flex-1">
                    {variants.map((v) => (
                      <button disabled key={v.id} className={`flex w-28 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${v.className}`}>
                        <v.icon size={14} />
                        {v.label}
                      </button>
                    ))}
                  </div>
                  <div className="hidden xl:block h-28 w-px bg-gray-200 shrink-0"></div>
                  <div className="flex flex-wrap items-center gap-3 xl:max-w-[200px]">
                    {variants.map((v) => (
                      <button disabled key={`icon-${v.id}`} className={`flex items-center justify-center rounded-md p-1.5 transition disabled:cursor-not-allowed disabled:opacity-50 ${v.className}`}>
                        <v.icon size={14} />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </UISection>

        {/* İkonlar Bölümü */}
        <UISection title="Aksiyon İkonları (Sade/Ghost)" isOpen={openSections.icons} onToggle={() => toggleSection('icons')}>
          <div className="flex flex-col gap-6">
            <p className="text-sm text-gray-500">
              Arka plansız, sadece üzerine gelince belirginleşen ikon buton tasarımları. Genellikle tablo satırları, kart üst menüleri gibi alanlarda kullanılır.
            </p>
            <div className="flex flex-wrap items-center gap-6">
              {actionIcons.map((ic) => (
                <div key={ic.id} className="flex flex-col items-center gap-2 w-16">
                  <button title={ic.label} className="flex items-center justify-center p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800 rounded-lg">
                    <ic.icon size={18} />
                  </button>
                  <span className="text-[10px] font-medium text-gray-400 text-center">{ic.label}</span>
                </div>
              ))}
            </div>
          </div>
        </UISection>

        {/* Rozetler Bölümü */}
        <UISection title="Rozetler (Badges)" isOpen={openSections.badges} onToggle={() => toggleSection('badges')}>
          <div className="flex flex-col gap-6">
            <p className="text-sm text-gray-500">
              Kişi, durum veya kategorileri belirten renkli rozet tasarımları. Formlar, tablolar ve kartlarda durumu hızlıca yansıtmak için idealdir.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1.5 text-xs font-semibold text-blue-700">
                <User size={14} />
                Kişi Adı
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700">
                <CheckCircle2 size={14} />
                Aktif
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-700">
                <XCircle size={14} />
                Pasif
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold text-amber-800">
                <AlertTriangle size={14} />
                Uyarı
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-100 px-3 py-1.5 text-xs font-semibold text-sky-700">
                <Info size={14} />
                Bilgi
              </span>

              <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 px-3 py-1.5 text-xs font-semibold text-purple-700">
                <Shield size={14} />
                Yetkili
              </span>
            </div>

            <p className="mt-2 text-sm text-gray-500 border-t pt-4">
              Daha köşeli, ikonsuz ince hatlı alternatifler (Etiket formatı):
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">Projeler</span>
              <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20">Tamamlandı</span>
              <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-600/20">Beklemede</span>
              <span className="inline-flex items-center rounded-md bg-rose-50 px-2 py-1 text-xs font-medium text-rose-700 ring-1 ring-inset ring-rose-600/10">İptal Edildi</span>
              <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">Taslak</span>
            </div>
          </div>
        </UISection>

        {/* Toasts Bölümü */}
        <UISection title="Bildirimler (Toasts)" isOpen={openSections.toasts} onToggle={() => toggleSection('toasts')}>
          <div className="flex flex-col gap-6">
            <p className="text-sm text-gray-500">
              Kullanıcı eylemleri sonucu ekranın köşesinde sistem tarafından gösterilen anlık bildirim kartları.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Başarılı */}
              <div className="flex w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5">
                <div className="flex w-12 shrink-0 items-center justify-center bg-emerald-500">
                  <CheckCircle2 className="h-6 w-6 text-white" />
                </div>
                <div className="px-4 py-3 flex-1">
                  <p className="text-sm font-semibold text-gray-800">Başarılı</p>
                  <p className="mt-1 text-xs text-gray-500">Kayıt işlemi başarıyla tamamlandı.</p>
                </div>
                <button className="flex items-start px-3 py-3 text-gray-400 hover:text-gray-600 transition">
                  <X size={16} />
                </button>
              </div>

              {/* Hata */}
              <div className="flex w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5">
                <div className="flex w-12 shrink-0 items-center justify-center bg-rose-500">
                  <XCircle className="h-6 w-6 text-white" />
                </div>
                <div className="px-4 py-3 flex-1">
                  <p className="text-sm font-semibold text-gray-800">Hata!</p>
                  <p className="mt-1 text-xs text-gray-500">Sunucu ile bağlantı sağlanamadı.</p>
                </div>
                <button className="flex items-start px-3 py-3 text-gray-400 hover:text-gray-600 transition">
                  <X size={16} />
                </button>
              </div>

              {/* Uyarı */}
              <div className="flex w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5">
                <div className="flex w-12 shrink-0 items-center justify-center bg-amber-500">
                  <AlertTriangle className="h-6 w-6 text-white" />
                </div>
                <div className="px-4 py-3 flex-1">
                  <p className="text-sm font-semibold text-gray-800">Dikkat</p>
                  <p className="mt-1 text-xs text-gray-500">Bu işlemi geri alamazsınız.</p>
                </div>
                <button className="flex items-start px-3 py-3 text-gray-400 hover:text-gray-600 transition">
                  <X size={16} />
                </button>
              </div>

              {/* Bilgi */}
              <div className="flex w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-black/5">
                <div className="flex w-12 shrink-0 items-center justify-center bg-blue-500">
                  <Info className="h-6 w-6 text-white" />
                </div>
                <div className="px-4 py-3 flex-1">
                  <p className="text-sm font-semibold text-gray-800">Bilgilendirme</p>
                  <p className="mt-1 text-xs text-gray-500">Sistem 5 dakika içinde güncellenecek.</p>
                </div>
                <button className="flex items-start px-3 py-3 text-gray-400 hover:text-gray-600 transition">
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Confirmation Toasts (Ekranın üst ortasında gösterim simülasyonu) */}
            <div className="mt-8 border-t border-gray-100 pt-8">
              <h3 className="mb-4 text-sm font-semibold text-gray-500 uppercase tracking-wider">Onay Bildirimleri (Confirmation Toasts)</h3>
              <p className="mb-6 text-sm text-gray-500">
                Kritik eylemler öncesinde (Silme veya Kaydetme) kullanıcı onayı alan ve sayfanın en üstünden (top-center) animasyonla açılan tasarımlar.
              </p>

              {/* Mock Ekran Simülasyonu */}
              <div className="relative h-[560px] w-full rounded-2xl border border-gray-200 bg-slate-50/50 overflow-hidden shadow-inner flex flex-col items-center">
                {/* Sahte Tarayıcı/Sayfa Barı */}
                <div className="h-10 w-full border-b border-gray-200 bg-white/80 backdrop-blur flex items-center px-4 gap-2 absolute top-0 left-0 z-0">
                  <div className="w-3 h-3 rounded-full bg-rose-400"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                  <span className="ml-4 text-[10px] font-medium text-gray-400 tracking-wider">SYSTEM PREVIEW</span>
                </div>

                {/* Toasts'un Konumlandığı Alan (top-center simülasyonu) */}
                <div className="absolute left-1/2 top-16 -translate-x-1/2 z-20 flex w-full max-w-md flex-col gap-6">

                  {/* Delete Confirmation Toast */}
                  <div className="flex flex-col w-full overflow-hidden rounded-xl bg-white shadow-2xl shadow-rose-900/5 ring-1 ring-black/5 transform transition-all">
                    <div className="p-5 flex items-start gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100">
                        <Trash2 className="h-5 w-5 text-rose-600" />
                      </div>
                      <div className="flex-1 pt-0.5">
                        <p className="text-base font-semibold text-gray-900">Kaydı Silmek İstediğinize Emin Misiniz?</p>
                        <p className="mt-1 text-sm text-gray-500">
                          Bu işlem geri alınamaz. Kayıt ve ilişkili tüm veriler kalıcı olarak sistemden silinecektir.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-gray-50/80 px-5 py-3 justify-end border-t border-gray-100">
                      <button className="flex w-32 items-center justify-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition">
                        <Ban size={16} className="text-gray-500" />
                        İptal
                      </button>
                      <button className="flex w-32 items-center justify-center gap-1.5 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-rose-500 transition">
                        <Check size={16} />
                        Evet
                      </button>
                    </div>
                  </div>

                  {/* Save Confirmation Toast */}
                  <div className="flex flex-col w-full overflow-hidden rounded-xl bg-white shadow-2xl shadow-emerald-900/5 ring-1 ring-black/5 transform transition-all">
                    <div className="p-5 flex items-start gap-4">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                        <Save className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 pt-0.5">
                        <p className="text-base font-semibold text-gray-900">Değişiklikleri Onaylıyor musunuz?</p>
                        <p className="mt-1 text-sm text-gray-500">
                          Yapılan tüm değişiklikler güncellenerek sistem veri tabanına işlenecektir. Devam edilsin mi?
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-gray-50/80 px-5 py-3 justify-end border-t border-gray-100">
                      <button className="flex w-32 items-center justify-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition">
                        <Ban size={16} className="text-gray-500" />
                        İptal
                      </button>
                      <button className="flex w-32 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-500 transition">
                        <CheckCircle2 size={16} />
                        Kaydet
                      </button>
                    </div>
                  </div>

                </div>

                {/* Arka plan dolgu metni/şekli (simülasyon hissiyatı için) */}
                <div className="mt-48 w-full max-w-2xl px-8 flex flex-col gap-4 text-center z-0 opacity-50">
                  <div className="h-32 rounded-xl border-2 border-dashed border-gray-300 bg-gray-100/50 flex flex-col items-center justify-center">
                    <span className="text-gray-400 font-medium">Sayfa İçeriği Bölgesii</span>
                    <span className="text-gray-400 text-xs mt-2">Diyaloglar sayfa içeriğinin üzerine (fixed absolute) olarak bindirilir.</span>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </UISection>

        {/* Tablolar Bölümü */}
        <UISection title="Tablolar (AG Grid Enterprise)" isOpen={openSections.tables} onToggle={() => toggleSection('tables')}>
          <div className="flex flex-col gap-6">
            <p className="text-sm text-gray-500">
              Proje genelindeki veri listelemelerinde `AG Grid Enterprise`'in Quartz-Light Cold teması kullanılmaktadır. Sütun panoları, range selection (hücre seçimi) ve gelişmiş filtreler gibi enterprise özelliklerini sağlar:
            </p>
            <div className="h-96 w-full shadow border border-gray-200 rounded-lg overflow-hidden">
              <AgGridReact
                modules={enterpriseModules}
                theme={agTheme}
                rowData={dummyRowData}
                columnDefs={dummyColDefs}
                defaultColDef={{ resizable: true, sortable: true, filter: true, enableRowGroup: true, enablePivot: true }}
                enableRangeSelection={true}
                rowSelection="multiple"
                sideBar={{
                  toolPanels: [
                    {
                      id: 'columns',
                      labelDefault: 'Sütunlar',
                      labelKey: 'columns',
                      iconKey: 'columns',
                      toolPanel: 'agColumnsToolPanel',
                    },
                    {
                      id: 'filters',
                      labelDefault: 'Filtreler',
                      labelKey: 'filters',
                      iconKey: 'filter',
                      toolPanel: 'agFiltersToolPanel',
                    },
                  ],
                }}
              />
            </div>
          </div>
        </UISection>

        {/* Modals Bölümü */}
        <UISection title="Modallar (Diyalog Penceresi)" isOpen={openSections.modals} onToggle={() => toggleSection('modals')}>
          <div className="flex flex-col gap-6">
            <p className="text-sm text-gray-500">
              Sistem genelinde kullanılan standart modal (popup) referans tasarımı. (Arka plan maskesi ile önizlenmektedir)
            </p>

            <div className="relative h-[600px] w-full rounded-2xl border border-gray-200 bg-slate-100 overflow-hidden flex items-center justify-center p-4">
              {/* Fake Backdrop */}
              <div className="absolute inset-0 bg-slate-900/30 backdrop-blur-[2px] z-0 pointer-events-none"></div>

              {/* Modal Container */}
              <div className="relative z-10 w-full max-w-2xl bg-white rounded-[16px] shadow-2xl ring-1 ring-black/5 overflow-hidden flex flex-col transform transition-all">

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-white">
                  <h3 className="text-lg font-semibold text-gray-800">Santral Yapılandırması</h3>

                  {/* Header Actions */}
                  <div className="flex items-center gap-1 relative">

                    {/* 3-Nokta Menü (Örnek Açılır Liste ile) */}
                    <div className="relative">
                      <button title="Daha Fazla İşlem" className="rounded-lg p-1.5 text-gray-700 bg-gray-100 transition ring-2 ring-gray-200">
                        <MoreVertical size={20} />
                      </button>

                      {/* Açılır Menü (Dropdown Popover Simülasyonu) */}
                      <div className="absolute right-0 top-full mt-1.5 w-48 rounded-lg bg-white py-1.5 shadow-lg ring-1 ring-black/5 z-50 animate-in fade-in slide-in-from-top-2">
                        <button className="flex w-full items-center gap-2.5 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
                          <History size={16} className="text-gray-400" />
                          Değişiklik Geçmişi
                        </button>
                        <button className="flex w-full items-center gap-2.5 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition">
                          <Copy size={16} className="text-gray-400" />
                          Kopyasını Oluştur
                        </button>

                        <div className="my-1.5 h-px bg-gray-100 w-[calc(100%-16px)] mx-auto"></div>

                        <button className="flex w-full items-center gap-2.5 px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 transition">
                          <Trash2 size={16} className="text-rose-500" />
                          Kaydı Kalıcı Sil
                        </button>
                      </div>
                    </div>

                    <button title="Kapat" className="rounded-lg p-1.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600 transition ml-1">
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Body / Content */}
                <div className="p-6 bg-slate-50/30 min-h-[220px]">
                  <p className="text-sm text-gray-500 mb-6">
                    Bu alana form elemanları (input, dropdown), açıklamalar veya daha farklı UI bileşenleri gelebilir. İçerik uzun olursa bu alan dikey olarak kaydırılır (overflow-y-auto).
                  </p>

                  {/* Fake Skeleton Content */}
                  <div className="flex flex-col gap-4 w-full">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-gray-600">Santral Adı</label>
                      <div className="w-full h-10 bg-white border border-gray-200 rounded-lg shadow-sm"></div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-gray-600">Kapasite (MW)</label>
                      <div className="w-2/3 h-10 bg-white border border-gray-200 rounded-lg shadow-sm"></div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100 bg-gray-50/50 gap-3">
                  <button className="flex w-32 items-center justify-center gap-1.5 rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 transition">
                    <Ban size={16} className="text-gray-500" />
                    İptal
                  </button>
                  <button className="flex w-32 items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 transition">
                    <CheckCircle2 size={16} />
                    Kaydet
                  </button>
                </div>

              </div>
            </div>

          </div>
        </UISection>

        {/* Sekmeler Bölümü */}
        <UISection title="Sekmeler (Tabs)" isOpen={openSections.tabs} onToggle={() => toggleSection('tabs')}>
          <div className="flex flex-col gap-10">

            {/* 1. Global App Tabs */}
            <div className="flex flex-col gap-4">
              <h5 className="text-sm font-bold text-gray-700">Genel Uygulama Sekmeleri (Underline Style)</h5>
              <div className="w-full bg-white border-b border-gray-200">
                <nav className="flex gap-8 px-2 overflow-x-auto scrollbar-hide">
                  {[
                    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
                    { id: 'inverters', label: 'İnverterler', icon: Shield }, // Sizin Shield ikonu projenizde inverteri temsil ediyor olabilir. Diğer sayfalarda kullanmışsınız.
                    { id: 'users', label: 'Kullanıcılar', icon: User },
                    { id: 'settings', label: 'Ayarlar', icon: Settings },
                  ].map((t) => {
                    const Icon = t.icon;
                    const isActive = activeGlobalTab === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setActiveGlobalTab(t.id)}
                        className={`group relative flex items-center gap-2 py-4 px-1 text-sm font-medium transition-all ${
                          isActive ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <Icon size={18} className={isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'} />
                        {t.label}
                        {isActive && (
                          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full shadow-[0_-2px_6px_rgba(37,99,235,0.4)]" />
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* 2. Side/Badge Tabs (Zaten var olan) */}
            <div className="flex flex-col gap-4">
              <h5 className="text-sm font-bold text-gray-700">Kart Detayı / Yan Panel Sekmeleri (Badge Style)</h5>
              <div className="w-full max-w-2xl bg-white p-6 border border-gray-100 rounded-2xl shadow-sm">
                <div className="flex gap-2 rounded-2xl border border-slate-200 bg-slate-100 p-1 w-full overflow-x-auto">
                  {([
                    ['comments', 'Yorumlar', MessageSquare, 5],
                    ['attachments', 'Ekler', Paperclip, 2],
                    ['relations', 'İlişkili Görevler', GitBranch, 0],
                    ['history', 'Geçmiş', History, 12],
                  ] as const).map(([key, label, Icon, count]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActiveTab(key)}
                      className={`flex-1 rounded-xl px-3 py-2.5 text-xs font-semibold transition whitespace-nowrap min-w-max ${
                        activeTab === key
                          ? 'bg-white text-slate-900 shadow-sm ring-1 ring-black/5'
                          : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'
                      }`}
                    >
                      <span className="flex items-center justify-center gap-1.5">
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{label}</span>
                        <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] leading-none transition ${activeTab === key ? 'bg-slate-100 text-slate-700' : 'bg-white/60 text-slate-500'}`}>
                          {count}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>

                <div className="mt-6 h-32 w-full rounded-xl border border-dashed border-gray-200 bg-gray-50 flex items-center justify-center text-sm text-gray-400">
                  "{activeTab}" sekmesinin içeriği burada görünecek.
                </div>
              </div>
            </div>

          </div>
        </UISection>

        {/* Formlar Bölümü */}
        <UISection title="Form Elemanları" isOpen={openSections.forms} onToggle={() => toggleSection('forms')}>
          <div className="flex flex-col gap-8 w-full max-w-4xl">
            <p className="text-sm text-gray-500">
              Uygulama genelinde kullanılacak veri girişi bileşenleri (Taslak form düzeni).
            </p>

            <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                {/* Text Input */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Kullanıcı Adı</label>
                  <div className="relative group">
                    <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                    <input 
                      type="text" 
                      placeholder="Örn: gesmasterinfo" 
                      className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-400" 
                    />
                  </div>
                </div>

                {/* Select / Dropdown */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Rol Seçimi</label>
                  <div className="relative group">
                    <Shield size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors pointer-events-none" />
                    <select className="w-full appearance-none rounded-lg border border-gray-300 bg-white pl-10 pr-10 py-2 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
                      <option value="">Lütfen seçiniz...</option>
                      <option value="admin">Yönetici</option>
                      <option value="editor">Editör</option>
                      <option value="viewer">Görüntüleyici</option>
                    </select>
                    <ChevronDown size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Date Picker */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">Başlangıç Tarihi</label>
                  <div className="relative group">
                    <Calendar size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors pointer-events-none" />
                    <input 
                      type="date" 
                      className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 py-2 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500" 
                    />
                  </div>
                </div>

                {/* Text Input (Error State) */}
                <div className="flex flex-col gap-1.5 md:col-span-1">
                  <label className="text-sm font-medium text-gray-700">E-Posta Adresi</label>
                  <div className="relative group">
                    <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-rose-400 transition-colors" />
                    <input 
                      type="email" 
                      defaultValue="yanlis.eposta@" 
                      className="w-full rounded-lg border border-rose-300 bg-rose-50 pl-10 pr-3 py-2 text-sm text-rose-900 outline-none transition focus:border-rose-500 focus:ring-1 focus:ring-rose-500" 
                    />
                  </div>
                  <span className="text-xs text-rose-500 font-medium mt-0.5">Lütfen geçerli bir e-posta adresi giriniz.</span>
                </div>

                {/* Textarea */}
                <div className="flex flex-col gap-1.5 md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Hakkında (Biyografi)</label>
                  <textarea 
                    rows={4} 
                    placeholder="Kendinizden kısaca bahsediniz..." 
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-y"
                  ></textarea>
                </div>

                {/* Multi Select with Checkboxes */}
                <MultiSelectDemo />

                {/* Multi Select with External Chips */}
                <MultiSelectChipsDemo />

                {/* Checkboxes */}
                <div className="flex flex-col gap-3">
                  <label className="text-sm font-medium text-gray-700">Kullanıcı Tercihleri</label>
                  <div className="flex flex-col gap-2.5">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input type="checkbox" defaultChecked className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition group-hover:border-blue-500" />
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition">Email bildirimleri al</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 transition group-hover:border-blue-500" />
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition">İki aşamalı doğrulama (2FA) aktifleştir</span>
                    </label>
                  </div>
                </div>

                {/* Radio Buttons */}
                <div className="flex flex-col gap-3">
                  <label className="text-sm font-medium text-gray-700">Hesap Durumu</label>
                  <div className="flex items-center gap-5">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input type="radio" name="account_status" defaultChecked className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500 transition group-hover:border-blue-500" />
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition">Aktif</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input type="radio" name="account_status" className="h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500 transition group-hover:border-blue-500" />
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition">Askıya Alınmış</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer group text-gray-400">
                      <input type="radio" name="account_status" disabled className="h-4 w-4 border-gray-200 text-gray-400 focus:ring-blue-500" />
                      <span className="text-sm">Silinmiş</span>
                    </label>
                  </div>
                </div>

                {/* Switcher (Toggle) */}
                <div className="flex flex-col gap-3 md:col-span-2">
                  <label className="text-sm font-medium text-gray-700">Sistem Tercihi</label>

                  <label className="relative inline-flex items-center cursor-pointer w-fit">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    <span className="ml-3 text-sm font-medium text-gray-700 select-none">Web Push (Tarayıcı Bildirimleri)</span>
                  </label>
                </div>

                {/* Maintenance Contract Form Part (Complex Example) */}
                <div className="md:col-span-2 mt-4 pt-6 border-t border-gray-100">
                  <h5 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <FileStack size={18} className="text-blue-600" />
                    Bakım Sözleşmesi Form Taslağı (Örnek Yapı)
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5 bg-slate-50/50 p-5 rounded-2xl border border-slate-100">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sözleşme Türü</label>
                      <select className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all">
                        <option>İzleme & Bakım</option>
                        <option>Sadece İzleme</option>
                        <option>Danışmanlık</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Aşama</label>
                      <select className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all">
                        <option>Taslak</option>
                        <option>Teklif Sunuldu</option>
                        <option>Onaylandı</option>
                      </select>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Referans Kodu</label>
                      <input type="text" placeholder="SVC-2026-001" className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
                    </div>
                    <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-5 mt-2">
                       <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Yatırımcı</label>
                        <input type="text" placeholder="Yatırımcı adını giriniz..." className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Süre (Yıl)</label>
                        <div className="flex items-center gap-3">
                          <input type="number" defaultValue={2} className="w-20 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
                          <span className="text-sm text-slate-400">Yıllık sözleşme süresi</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

            </div>
          </div>
        </UISection>

        {/* Paneller Bölümü */}
        <UISection title="Paneller & Yan Akışlar" isOpen={openSections.panels} onToggle={() => toggleSection('panels')}>
          <div className="flex flex-col gap-8 w-full">
            <p className="text-sm text-gray-500">
              Agile Board veya detay sayfalarında kullanılan, tarihçe (history) ve yorum akışlarını içeren yan panel tasarımları.
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

              {/* Yorumlar Paneli */}
              <div className="flex flex-col h-[500px] bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                  <div className="flex items-center gap-2">
                    <MessageSquare size={18} className="text-gray-500" />
                    <h4 className="text-sm font-semibold text-gray-800">Yorumlar</h4>
                    <span className="bg-gray-200 text-gray-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full">3</span>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-white">
                  {/* Yorum 1 */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-900">gesmasterinfo</span>
                      <span className="text-[10px] text-gray-400">12:45</span>
                    </div>
                    <div className="bg-slate-50 rounded-2xl rounded-tl-none p-3 border border-slate-100 text-sm text-gray-700 leading-relaxed">
                      Sinyal seviyeleri normale döndü, inverter 3 üzerinden kontroller sağlandı.
                    </div>
                  </div>

                  {/* Yorum 2 */}
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-gray-900">Ahmet Yılmaz</span>
                      <span className="text-[10px] text-gray-400">Dün 16:20</span>
                    </div>
                    <div className="bg-blue-50 rounded-2xl rounded-tl-none p-3 border border-blue-100 text-sm text-gray-700 leading-relaxed shadow-sm">
                      <span className="text-blue-600 font-medium">@gesmasterinfo</span> Lütfen log kayıtlarını da ekleyelim.
                    </div>
                  </div>
                </div>

                <div className="p-4 border-t border-gray-100 bg-white">
                  <div className="relative flex items-center gap-2">
                    <input 
                      type="text" 
                      placeholder="Bir yorum yazın..." 
                      className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
                    />
                    <button className="bg-cyan-600 hover:bg-cyan-700 text-white p-2.5 rounded-xl transition-colors shadow-sm">
                      <Send size={18} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Geçmiş / History Paneli */}
              <div className="flex flex-col h-[500px] bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                  <div className="flex items-center gap-2">
                    <History size={18} className="text-gray-500" />
                    <h4 className="text-sm font-semibold text-gray-800">İşlem Geçmişi</h4>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5">
                  <div className="relative space-y-6 before:absolute before:inset-0 before:ml-2.5 before:h-full before:w-0.5 before:bg-gray-100">

                    {/* History Item 1 */}
                    <div className="relative pl-8">
                      <div className="absolute left-0 top-1 h-5 w-5 rounded-full border-4 border-white bg-blue-500 shadow-sm z-10"></div>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-gray-900">Durum Değişti</span>
                          <span className="text-[10px] text-gray-400">10 dk önce</span>
                        </div>
                        <p className="text-xs text-gray-600">
                          <span className="font-medium text-gray-800">gesmasterinfo</span> durumu <span className="text-blue-600 font-semibold italic">İncelemede</span> olarak güncelledi.
                        </p>
                      </div>
                    </div>

                    {/* History Item 2 */}
                    <div className="relative pl-8">
                      <div className="absolute left-0 top-1 h-5 w-5 rounded-full border-4 border-white bg-emerald-500 shadow-sm z-10"></div>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-gray-900">Dosya Eklendi</span>
                          <span className="text-[10px] text-gray-400">1 saat önce</span>
                        </div>
                        <p className="text-xs text-gray-600">
                          <span className="font-medium text-gray-800">Sistem</span> tarafından <span className="underline cursor-pointer text-gray-700">rapor_final.pdf</span> dosyası eklendi.
                        </p>
                      </div>
                    </div>

                    {/* History Item 3 */}
                    <div className="relative pl-8">
                      <div className="absolute left-0 top-1 h-5 w-5 rounded-full border-4 border-white bg-gray-400 shadow-sm z-10"></div>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-gray-900">Görev Atandı</span>
                          <span className="text-[10px] text-gray-400">3 saat önce</span>
                        </div>
                        <p className="text-xs text-gray-600">
                          Bu görev <span className="font-medium text-gray-800">Ahmet Yılmaz</span> kullanıcısına atandı.
                        </p>
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              {/* 3. Button Groups (Segmented Control) */}
              <div className="lg:col-span-2 bg-white p-6 border border-gray-100 rounded-3xl shadow-sm">
                <h5 className="text-sm font-bold text-gray-800 mb-6">Buton Grupları (Segmented Control)</h5>
                <div className="flex flex-wrap gap-8">

                  {/* Basic Group */}
                  <div className="flex flex-col gap-3">
                    <span className="text-xs font-semibold text-gray-400">Basic Toggle</span>
                    <div className="inline-flex rounded-xl bg-gray-100 p-1">
                      <button className="rounded-lg bg-white px-4 py-1.5 text-xs font-bold text-gray-900 shadow-sm transition">Günlük</button>
                      <button className="rounded-lg px-4 py-1.5 text-xs font-bold text-gray-500 hover:text-gray-700 transition">Aylık</button>
                      <button className="rounded-lg px-4 py-1.5 text-xs font-bold text-gray-500 hover:text-gray-700 transition">Yıllık</button>
                    </div>
                  </div>

                  {/* Icon Group */}
                  <div className="flex flex-col gap-3">
                    <span className="text-xs font-semibold text-gray-400">View Switcher</span>
                    <div className="inline-flex rounded-xl border border-gray-200">
                      <button className="flex items-center gap-2 border-r border-gray-200 bg-blue-50 px-4 py-2 text-xs font-bold text-blue-600 transition first:rounded-l-xl">
                         <LayoutDashboard size={14} />
                         Liste
                      </button>
                      <button className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-gray-500 hover:bg-gray-50 transition last:rounded-r-xl">
                         <Globe2 className="h-3.5 w-3.5" />
                         Harita
                      </button>
                    </div>
                  </div>

                  {/* Filter Group */}
                  <div className="flex flex-col gap-3">
                    <span className="text-xs font-semibold text-gray-400">Status Filter</span>
                    <div className="inline-flex divide-x divide-gray-200 rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                      <button className="bg-white px-5 py-2 text-xs font-semibold text-emerald-600 hover:bg-emerald-50 transition">Aktif</button>
                      <button className="bg-white px-5 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 transition">Pasif</button>
                      <button className="bg-white px-5 py-2 text-xs font-semibold text-amber-600 hover:bg-amber-50 transition">Beklemede</button>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </UISection>

        {/* Etiketler Bölümü */}
        <UISection title="Etiketler (Tags)" isOpen={openSections.tags} onToggle={() => toggleSection('tags')}>
          <div className="flex flex-col gap-10">

            {/* 1. Tag Color Dots (Table Style) */}
            <div className="flex flex-col gap-4">
              <h5 className="text-sm font-bold text-gray-700">Tablo İçi Renkli Nokta Etiketler (Tag Color Dots)</h5>
              <p className="text-xs text-gray-500">Santraller tablosunda kullanılan, üzerine gelince parlayan (radial gradient) küre etiketler.</p>
              <div className="flex items-center gap-4 bg-white p-6 border border-gray-100 rounded-2xl shadow-sm w-fit">
                {[
                  { id: 1, label: 'Kritik', color: '#ef4444' },
                  { id: 2, label: 'Uyarı', color: '#f59e0b' },
                  { id: 3, label: 'Normal', color: '#10b981' },
                  { id: 4, label: 'Bilgi', color: '#3b82f6' },
                  { id: 5, label: 'Bakım', color: '#8b5cf6' },
                ].map(tag => (
                  <div key={tag.id} className="group relative flex flex-col items-center gap-2">
                    <span
                      className="inline-flex rounded-full border border-white/70 shadow-sm ring-1 ring-black/5"
                      style={{
                        width: '20px',
                        height: '20px',
                        background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95) 0%, ${tag.color} 42%, ${tag.color}DD 72%, rgba(15,23,42,0.24) 100%)`,
                        boxShadow: `inset 0 1px 1px rgba(255,255,255,0.45), inset 0 -1px 2px rgba(15,23,42,0.18), 0 1px 2px rgba(15,23,42,0.12)`,
                      }}
                    />
                    <span className="text-[10px] font-medium text-gray-500">{tag.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 2. Tag Filter Buttons */}
            <div className="flex flex-col gap-4">
              <h5 className="text-sm font-bold text-gray-700">Filtre Butonları (Tag Filter Style)</h5>
              <p className="text-xs text-gray-500">Sayfa üstlerinde kullanılan, seçildiğinde kendi renginde parlayan interaktif filtre butonları.</p>
              <div className="flex flex-wrap gap-2 bg-white p-6 border border-gray-100 rounded-2xl shadow-sm">
                {[
                  { id: 1, label: 'Kritik Arıza', color: '#ef4444' },
                  { id: 2, label: 'Düşük Verim', color: '#f59e0b' },
                  { id: 3, label: 'Aktif Santraller', color: '#10b981' },
                  { id: 4, label: 'Planlı Kesinti', color: '#3b82f6' },
                ].map((tag) => {
                  const isActive = selectedTags.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition shadow-sm ${
                        isActive
                          ? 'ring-1 ring-black/5'
                          : 'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700'
                      }`}
                      style={isActive ? {
                        borderColor: tag.color,
                        background: `linear-gradient(180deg, ${tag.color}22 0%, ${tag.color}12 100%)`,
                        color: tag.color,
                      } : undefined}
                    >
                      <span
                        className="inline-flex rounded-full border border-white/70 shadow-sm ring-1 ring-black/5 w-3 h-3"
                        style={{
                          background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.95) 0%, ${tag.color} 42%, ${tag.color}DD 72%, rgba(15,23,42,0.24) 100%)`,
                        }}
                      />
                      <span>{tag.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

          </div>
        </UISection>

        {/* Grafikler Bölümü */}
        <UISection title="Grafikler (Charts)" isOpen={openSections.charts} onToggle={() => toggleSection('charts')}>
          <div className="flex flex-col gap-10">
            <p className="text-sm text-gray-500">
              Veri görselleştirme ve analiz için kullanılan temel grafik tipleri.
            </p>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 w-full">

              {/* 1. Multi Line Chart */}
              <div className="bg-white p-6 border border-gray-100 rounded-3xl shadow-sm">
                <h5 className="text-sm font-bold text-gray-800 mb-6 flex items-center justify-between">
                  <span>Üretim Verileri (Multi Line Chart)</span>
                  <span className="text-[10px] text-gray-400 font-normal">Son 7 Gün</span>
                </h5>
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={[
                      { day: 'Pzt', ges1: 400, ges2: 240 },
                      { day: 'Sal', ges1: 300, ges2: 139 },
                      { day: 'Çar', ges1: 200, ges2: 980 },
                      { day: 'Per', ges1: 278, ges2: 390 },
                      { day: 'Cum', ges1: 189, ges2: 480 },
                      { day: 'Cmt', ges1: 239, ges2: 380 },
                      { day: 'Paz', ges1: 349, ges2: 430 },
                    ]}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} dy={10} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip 
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                        itemStyle={{ fontSize: '12px', fontWeight: '600' }}
                      />
                      <Legend verticalAlign="top" align="right" iconType="circle" wrapperStyle={{ fontSize: '11px', paddingBottom: '20px' }} />
                      <Line type="monotone" dataKey="ges1" name="Alfa GES" stroke="#3b82f6" strokeWidth={3} dot={{ r: 4, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 6 }} />
                      <Line type="monotone" dataKey="ges2" name="Beta GES" stroke="#10b981" strokeWidth={3} dot={{ r: 4, strokeWidth: 2, fill: '#fff' }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* 2. Single Area Chart */}
              <div className="bg-white p-6 border border-gray-100 rounded-3xl shadow-sm">
                <h5 className="text-sm font-bold text-gray-800 mb-6">Tüketim Analizi (Area Chart)</h5>
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={[
                      { h: '08:00', v: 45 }, { h: '10:00', v: 80 }, { h: '12:00', v: 120 },
                      { h: '14:00', v: 90 }, { h: '16:00', v: 60 }, { h: '18:00', v: 30 },
                    ]}>
                      <defs>
                        <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.1}/>
                          <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="h" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#64748b' }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="v" name="kW" stroke="#8b5cf6" strokeWidth={2} fillOpacity={1} fill="url(#colorValue)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* 3. Stacked Bar Chart */}
              <div className="bg-white p-6 border border-gray-100 rounded-3xl shadow-sm">
                <h5 className="text-sm font-bold text-gray-800 mb-6">Yatırımcı Bazlı Dağılım (Stacked Bar)</h5>
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={[
                      { category: 'A Bölgesi', active: 400, inactive: 240 },
                      { category: 'B Bölgesi', active: 300, inactive: 139 },
                      { category: 'C Bölgesi', active: 200, inactive: 980 },
                    ]} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                      <XAxis type="number" axisLine={false} tickLine={false} hide />
                      <YAxis dataKey="category" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11, fontWeight: '600', fill: '#475569' }} width={80} />
                      <Tooltip />
                      <Legend iconType="rect" />
                      <Bar dataKey="active" name="Aktif" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} barSize={20} />
                      <Bar dataKey="inactive" name="Pasif" stackId="a" fill="#f43f5e" radius={[0, 6, 6, 0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* 4. Pie Chart with Legend */}
              <div className="bg-white p-6 border border-gray-100 rounded-3xl shadow-sm">
                <h5 className="text-sm font-bold text-gray-800 mb-6">Hava Durumu Dağılımı (Pie Chart)</h5>
                <div className="h-72 w-full flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Güneşli', value: 45, color: '#f59e0b' },
                          { name: 'Bulutlu', value: 30, color: '#94a3b8' },
                          { name: 'Yağmurlu', value: 15, color: '#3b82f6' },
                          { name: 'Fırtınalı', value: 10, color: '#ef4444' },
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {([
                          { name: 'Güneşli', value: 45, color: '#f59e0b' },
                          { name: 'Bulutlu', value: 30, color: '#94a3b8' },
                          { name: 'Yağmurlu', value: 15, color: '#3b82f6' },
                          { name: 'Fırtınalı', value: 10, color: '#ef4444' },
                        ]).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                         contentStyle={{ borderRadius: '12px', border: 'none' }}
                      />
                      <Legend verticalAlign="middle" align="right" layout="vertical" iconType="circle" />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

            </div>
          </div>
        </UISection>

        {/* Yüklenme & Bekleme Bölümü */}
        <UISection title="Yüklenme & Bekleme (Wait States)" isOpen={openSections.loading} onToggle={() => toggleSection('loading')}>
          <div className="flex flex-col gap-10">
            <p className="text-sm text-gray-500">
              Veri yüklenirken veya işlem yapılırken kullanıcıyı bilgilendiren modern bekleme arayüzleri.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">

              {/* 1. Standard Spinner Card */}
              <div className="flex flex-col items-center justify-center bg-white p-8 border border-gray-100 rounded-3xl shadow-sm min-h-[220px]">
                <div className="flex gap-4 mb-4">
                  <Loader2 className="h-10 w-10 text-blue-600 animate-spin" />
                  <RotateCw className="h-10 w-10 text-slate-300 animate-spin" style={{ animationDirection: 'reverse' }} />
                </div>
                <p className="text-sm font-semibold text-gray-800">Veriler Yükleniyor...</p>
                <p className="text-xs text-gray-400 mt-1">Lütfen bekleyiniz, bu işlem birkaç saniye sürebilir.</p>
              </div>

              {/* 2. Progress Bar Wait Form */}
              <div className="flex flex-col bg-white p-6 border border-gray-100 rounded-3xl shadow-sm min-h-[220px]">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center">
                    <RefreshCw className="h-5 w-5 text-amber-600 animate-spin" style={{ animationDuration: '3s' }} />
                  </div>
                  <div>
                    <h5 className="text-sm font-bold text-gray-800">Senkronizasyon</h5>
                    <p className="text-[11px] text-gray-400 font-medium">Santral verileri eşitleniyor</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-gray-600">İşlem Durumu</span>
                    <span className="font-bold text-blue-600">65%</span>
                  </div>
                  <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                    <div className="bg-blue-600 h-full rounded-full transition-all duration-500" style={{ width: '65%' }}></div>
                  </div>
                  <div className="flex items-center gap-2 text-[10px] text-gray-400 italic">
                    <div className="h-1 w-1 rounded-full bg-gray-300 animate-bounce"></div>
                    İnverter kayıtları kontrol ediliyor...
                  </div>
                </div>
              </div>

              {/* 3. Skeleton Loader (Wait Form) */}
              <div className="flex flex-col bg-white p-6 border border-gray-100 rounded-3xl shadow-sm min-h-[220px] animate-pulse">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-10 w-10 rounded-full bg-gray-200"></div>
                  <div className="space-y-2 flex-1">
                    <div className="h-2.5 w-24 bg-gray-200 rounded-full"></div>
                    <div className="h-2 w-32 bg-gray-100 rounded-full"></div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="h-3 w-full bg-gray-100 rounded-lg"></div>
                  <div className="h-3 w-[85%] bg-gray-100 rounded-lg"></div>
                  <div className="h-3 w-[92%] bg-gray-100 rounded-lg"></div>
                  <div className="flex gap-2 mt-4">
                    <div className="h-8 w-20 bg-gray-200 rounded-xl"></div>
                    <div className="h-8 w-20 bg-gray-100 rounded-xl"></div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </UISection>

        {/* Veri Kartları & Özetler Bölümü */}
        <UISection title="Veri Kartları & Özetler (Stat Cards)" isOpen={openSections.stats} onToggle={() => toggleSection('stats')}>
          <div className="flex flex-col gap-10">
            <p className="text-sm text-gray-500">
              Dashboard ve ana sayfalarda kullanılan, anahtar verileri (KPI) özetleyen modern kart tasarımları.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">

              {/* Stat Card 1 - Production */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm relative group hover:shadow-md transition-shadow cursor-default">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Günlük Üretim</span>
                      <div className="text-2xl font-black text-gray-900">452.8 MWh</div>
                    </div>
                    <div className="h-10 w-10 rounded-xl bg-orange-50 flex items-center justify-center">
                      <Plus size={20} className="text-orange-500 rotate-45" /> {/* Güneşi simgeleyen ikon olarak */}
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-1.5">
                    <div className="flex items-center text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-lg text-[11px] font-bold">
                      <ArrowUpRight size={14} />
                      <span>8.2%</span>
                    </div>
                    <span className="text-[10px] text-gray-400">Dün ile karşılaştırıldığında</span>
                  </div>
              </div>

              {/* Stat Card 2 - Efficiency */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-default">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Aktiflik Oranı</span>
                      <div className="text-2xl font-black text-gray-900">98.2%</div>
                    </div>
                    <div className="h-10 w-10 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Shield size={20} className="text-blue-500" />
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-1.5">
                    <div className="flex items-center text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded-lg text-[11px] font-bold">
                      <ArrowDownRight size={14} />
                      <span>0.4%</span>
                    </div>
                    <span className="text-[10px] text-gray-400">Son 1 hafta ortalaması</span>
                  </div>
              </div>

               {/* Stat Card 3 - Revenue */}
               <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-default">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">Tahmini Kazanç</span>
                    <div className="text-2xl font-black text-gray-900">$12,450</div>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <CheckCircle2 size={20} className="text-emerald-500" />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-1.5">
                  <div className="flex items-center text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-lg text-[11px] font-bold">
                    <ArrowUpRight size={14} />
                    <span>12.5%</span>
                  </div>
                  <span className="text-[10px] text-gray-400">Bu ayki hedef</span>
                </div>
              </div>

              {/* Stat Card 4 - Dashboard Progress Style */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-default col-span-1">
                <div className="flex items-center gap-2 text-gray-800 mb-4">
                  <HousePlug className="h-5 w-5 text-green-700" />
                  <h2 className="text-sm font-bold">Santraller: 128</h2>
                </div>
                <div className="flex justify-between text-[10px] font-bold mb-2">
                  <span className="flex items-center gap-1 text-emerald-600"><Leaf className="h-3 w-3" />Aktif: 122</span>
                  <span className="flex items-center gap-1 text-rose-500"><AlertTriangle className="h-3 w-3" />Pasif: 6</span>
                </div>
                <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-2 rounded-full bg-emerald-500 transition-all duration-500" style={{ width: '95.3%' }} />
                </div>
                <p className="mt-2 text-right text-[10px] font-semibold text-gray-400">Aktiflik Oranı: %95.3</p>
              </div>

              {/* Stat Card 5 - Dashboard Alarm Style (Complex) */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-default col-span-1 md:col-span-2 xl:col-span-1">
                <div className="flex items-center gap-2 text-gray-800 mb-4">
                  <Bell className="h-5 w-5 text-rose-500" />
                  <h2 className="text-sm font-bold">Alarmlar: 42</h2>
                </div>
                <div className="flex h-5 w-full overflow-hidden rounded-full bg-gray-100 text-[9px] font-black select-none">
                  <div className="bg-amber-400 text-white flex items-center justify-center" style={{ width: '50%' }}>21</div>
                  <div className="bg-blue-400 text-white flex items-center justify-center" style={{ width: '30%' }}>12</div>
                  <div className="bg-rose-500 text-white flex items-center justify-center" style={{ width: '20%' }}>9</div>
                </div>
                <div className="flex gap-3 mt-3 flex-wrap">
                  <span className="flex items-center gap-1 text-[10px] font-bold text-gray-500">
                    <span className="h-2 w-2 rounded-full bg-amber-400" /> Uyarı: 21
                  </span>
                  <span className="flex items-center gap-1 text-[10px] font-bold text-gray-500">
                    <span className="h-2 w-2 rounded-full bg-blue-400" /> Minor: 12
                  </span>
                  <span className="flex items-center gap-1 text-[10px] font-bold text-gray-500">
                    <span className="h-2 w-2 rounded-full bg-rose-500" /> Major: 9
                  </span>
                </div>
              </div>

              {/* Stat Card 6 - Dashboard Task Grid Style */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-default col-span-1">
                <div className="flex items-center gap-2 text-gray-800 mb-4">
                  <ClipboardList className="h-5 w-5 text-blue-500" />
                  <h2 className="text-sm font-bold">Görevler: 18</h2>
                </div>
                <div className="grid grid-cols-2 gap-1.5 text-[9px] font-black text-white">
                  <div className="flex items-center justify-center bg-gray-400 rounded-lg py-1.5">Açık: 5</div>
                  <div className="flex items-center justify-center bg-amber-400 rounded-lg py-1.5">Süreçte: 8</div>
                  <div className="flex items-center justify-center bg-emerald-500 rounded-lg py-1.5">Bitti: 4</div>
                  <div className="flex items-center justify-center bg-rose-500 rounded-lg py-1.5">İptal: 1</div>
                </div>
              </div>

              {/* Stat Card 7 - Dashboard Energy Style */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-default col-span-1">
                <div className="flex items-center gap-2 text-gray-500 text-[10px] font-bold uppercase tracking-wider mb-3">
                  <Zap className="h-4 w-4 text-amber-400" />
                  <span>Günlük Enerji</span>
                </div>
                <div className="text-2xl font-black text-gray-900 leading-tight">1.24 GWh</div>
                <div className="text-[10px] text-gray-400 mt-2">Bugünkü toplam üretim</div>
              </div>

              {/* Stat Card 8 - Dashboard Sensor Style */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-default col-span-1">
                <div className="flex items-center gap-2 text-gray-800 mb-4">
                  <ThermometerSun className="h-5 w-5 text-rose-500" />
                  <h2 className="text-sm font-bold">Sensör Verileri</h2>
                </div>
                <div className="flex justify-between items-end">
                  <div>
                    <div className="text-2xl font-black text-gray-900">42.5°C</div>
                    <div className="text-[9px] font-bold text-gray-400">Panel Sıcaklığı</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xl font-bold text-blue-600">850 W/m²</div>
                    <div className="text-[9px] font-bold text-gray-400">Işınım</div>
                  </div>
                </div>
              </div>

              {/* Stat Card 9 - Dashboard CPU/HW Style */}
              <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-default col-span-1">
                <div className="flex items-center gap-2 text-gray-800 mb-4">
                  <Cpu className="h-5 w-5 text-indigo-500" />
                  <h2 className="text-sm font-bold">Donanım Durumu</h2>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between text-[10px] items-center">
                    <span className="text-gray-500 font-bold uppercase">CPU Load</span>
                    <span className="text-indigo-600 font-black">24%</span>
                  </div>
                  <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
                    <div className="bg-indigo-500 h-full w-1/4" />
                  </div>
                </div>
              </div>

            </div>
          </div>
        </UISection>

        {/* Dosya Yönetimi Bölümü */}
        <UISection title="Dosya Yönetimi (Uploads)" isOpen={openSections.uploads} onToggle={() => toggleSection('uploads')}>
          <div className="flex flex-col gap-10">
            <p className="text-sm text-gray-500">
              Bakım raporları, sözleşmeler ve dokümanlar için dosya yükleme ve önizleme bileşenleri.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

              {/* 1. Drag & Dropzone */}
              <div className="flex flex-col gap-4">
                <h5 className="text-sm font-bold text-gray-700">Dosya Yükleme Alanı</h5>
                <div className="border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50 p-10 flex flex-col items-center justify-center gap-4 transition hover:bg-slate-50 hover:border-blue-400 group cursor-pointer">
                  <div className="h-14 w-14 rounded-2xl bg-white shadow-sm flex items-center justify-center text-slate-400 group-hover:text-blue-500 transition-colors">
                    <Paperclip size={28} />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-gray-800">Dosyaları buraya bırakın</p>
                    <p className="text-xs text-gray-400 mt-1">Veya bilgisayarınızdan seçmek için <span className="text-blue-600 font-semibold underline">tıklayın</span></p>
                  </div>
                  <div className="px-3 py-1 rounded-full bg-slate-100 text-[10px] font-bold text-slate-500 tracking-tighter uppercase">
                    Max. 10MB - PDF, JPG, PNG
                  </div>
                </div>
              </div>

              {/* 2. File List Item */}
              <div className="flex flex-col gap-4">
                <h5 className="text-sm font-bold text-gray-700">Yüklenen Dosyalar</h5>
                <div className="space-y-2">
                  {[
                    { name: 'bakim_raporu_subat.pdf', size: '2.4 MB', type: 'pdf' },
                    { name: 'tesis_goruntusu_01.jpg', size: '4.1 MB', type: 'img' },
                  ].map((file, idx) => (
                    <div key={idx} className="flex items-center gap-4 bg-white border border-gray-100 p-3 rounded-2xl hover:border-blue-200 transition group shadow-sm">
                      <div className="h-10 w-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-blue-50 group-hover:text-blue-500 transition-colors">
                         <FileStack size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-800 truncate">{file.name}</div>
                        <div className="text-xs text-gray-400">{file.size}</div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button className="p-2 text-gray-400 hover:text-blue-600 transition" title="Önizle"><Eye size={16} /></button>
                        <button className="p-2 text-gray-400 hover:text-rose-600 transition" title="Sil"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </UISection>

        {/* Agile Board Kartları Bölümü */}
        <UISection title="Agile Board Kartları (Task Cards)" isOpen={openSections.agileCards} onToggle={() => toggleSection('agileCards')}>
          <div className="flex flex-col gap-10">
            <p className="text-sm text-gray-500">
              Görev yönetimi panelinde kullanılan, kategori ve öncelik bazlı renklenen interaktif kartlar.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">

              {/* 1. Geliştirme Kartı (Normal) */}
              <div className="flex flex-col gap-3">
                <span className="text-xs font-semibold text-gray-400">Geliştirme / Normal</span>
                <div 
                  className="w-full rounded-xl border p-3 text-left shadow-sm transition hover:-translate-y-1 hover:shadow-md cursor-pointer"
                  style={{ borderColor: '#2563eb', backgroundColor: 'rgba(37, 99, 235, 0.08)' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-blue-600">Sistem Yönetimi</div>
                      <div className="mt-0.5 text-[9px] font-bold text-blue-700">Geliştirme</div>
                      <h3 className="mt-1.5 text-xs font-bold leading-tight text-slate-800 line-clamp-2">
                        Kullanıcı yetkilendirme modülüne yeni rollerin eklenmesi
                      </h3>
                    </div>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-extrabold text-slate-600 uppercase">Düşük</span>
                  </div>
                  <div className="mt-2.5 space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                      <CircleUserRound size={12} className="text-slate-300" />
                      <span>Oluşturan: <span className="font-semibold text-slate-500">Ahmet Yılmaz</span></span>
                      <span className="mx-1">•</span>
                      <span>14.04.2026</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 border-t border-blue-100/50 pt-2.5 mt-2.5">
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-medium">
                        <div className="h-5 w-5 rounded-full bg-blue-600 flex items-center justify-center text-[8px] text-white font-black">MA</div>
                        <span className="truncate">Sorumlu: gesmasterinfo</span>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-400">
                        <div className="flex items-center gap-1"><MessageSquare size={12} /> <span>4</span></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 2. Hata Kartı (Yüksek Öncelik) */}
              <div className="flex flex-col gap-3">
                <span className="text-xs font-semibold text-gray-400">Hata / Acil</span>
                <div 
                  className="w-full rounded-xl border p-3 text-left shadow-sm transition hover:-translate-y-1 hover:shadow-md cursor-pointer"
                  style={{ borderColor: '#dc2626', backgroundColor: 'rgba(220, 38, 38, 0.08)' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-rose-600">Bakım Modülü</div>
                      <div className="mt-0.5 text-[9px] font-bold text-rose-700">Hata</div>
                      <h3 className="mt-1.5 text-xs font-bold leading-tight text-slate-800 line-clamp-2">
                        Sözleşme PDF oluşturma sırasında karakter kodlama hatası
                      </h3>
                    </div>
                    <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[9px] font-extrabold text-rose-700 uppercase">Yüksek</span>
                  </div>
                  <div className="mt-2.5 space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[10px] text-rose-400">
                      <CircleUserRound size={12} className="text-rose-300" />
                      <span>Oluşturan: <span className="font-semibold text-rose-500">Fatma Demir</span></span>
                      <span className="mx-1">•</span>
                      <span>Bugün 09:42</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 border-t border-rose-100/50 pt-2.5 mt-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold text-rose-600 bg-rose-50 px-2 py-0.5 rounded-full">Termin: Bugün</span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-500 font-medium">
                        <div className="h-5 w-5 rounded-full bg-rose-500 flex items-center justify-center text-[8px] text-white font-black">EK</div>
                        <span>Sorumlu: E. Kaya</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 3. İyileştirme Kartı */}
              <div className="flex flex-col gap-3">
                <span className="text-xs font-semibold text-gray-400">İyileştirme / Orta</span>
                <div 
                  className="w-full rounded-xl border p-3 text-left shadow-sm transition hover:-translate-y-1 hover:shadow-md cursor-pointer"
                  style={{ borderColor: '#16a34a', backgroundColor: 'rgba(22, 163, 74, 0.08)' }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-emerald-600">Santral İzleme</div>
                      <div className="mt-0.5 text-[9px] font-bold text-emerald-700">İyileştirme</div>
                      <h3 className="mt-1.5 text-xs font-bold leading-tight text-slate-800 line-clamp-2">
                        Grafik yüklenme performansının optimize edilmesi
                      </h3>
                    </div>
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[9px] font-extrabold text-amber-700 uppercase">Orta</span>
                  </div>
                  <div className="mt-2.5 space-y-1.5">
                    <div className="flex items-center gap-1.5 text-[10px] text-emerald-400">
                      <CircleUserRound size={12} className="text-emerald-300" />
                      <span>Oluşturan: <span className="font-semibold text-emerald-500">Caner Akın</span></span>
                      <span className="mx-1">•</span>
                      <span>12.04.2026</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-emerald-100/50 pt-2.5 mt-2.5">
                      <span className="text-[10px] text-slate-400 italic">Devam ediyor...</span>
                      <div className="flex items-center gap-2">
                         <span className="text-[10px] text-slate-500 font-medium">Sorumlular:</span>
                         <div className="flex -space-x-2">
                            <div className="h-5 w-5 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center text-[7px] font-bold">BK</div>
                            <div className="h-5 w-5 rounded-full border-2 border-white bg-slate-300 flex items-center justify-center text-[7px] font-bold">AS</div>
                         </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 4. Kapalı / İptal Edildi (Kompakt Tasarım) */}
              <div className="flex flex-col gap-3">
                <span className="text-xs font-semibold text-gray-400">Kapalı / İptal (Kompakt)</span>
                <div className="space-y-2">
                  {/* Closed Card */}
                  <div className="w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-left opacity-75 shadow-sm transition hover:opacity-100 cursor-pointer">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-[11px] font-bold leading-tight text-slate-500 line-clamp-1 flex-1">
                        Veri tabanı migration işlemi tamamlandı
                      </h3>
                      <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[8px] font-black text-slate-600 uppercase">KAPALI</span>
                    </div>
                  </div>
                  {/* Canceled Card */}
                  <div className="w-full rounded-lg border border-rose-100 bg-rose-50/30 p-2 text-left opacity-75 shadow-sm transition hover:opacity-100 cursor-pointer">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-[11px] font-bold leading-tight text-rose-800/60 line-clamp-1 flex-1">
                        Eski raporlama motoru desteği
                      </h3>
                      <span className="rounded-full bg-rose-100 px-1.5 py-0.5 text-[8px] font-black text-rose-700 uppercase">İPTAL</span>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </UISection>

        {/* Tarih Aralığı Seçici Bölümü */}
        <UISection title="Tarih Aralığı Seçici (Date Range Picker)" isOpen={openSections.dateRange} onToggle={() => toggleSection('dateRange')}>
          <div className="flex flex-col gap-10">
            <p className="text-sm text-gray-500">
              Raporlama ve veri filtreleme için kullanılan, hızlı seçim butonlarına sahip tarih aralığı paneli.
            </p>

            <div className="max-w-md w-full bg-white rounded-3xl border border-gray-100 shadow-xl overflow-hidden p-6 ring-1 ring-black/[0.03]">
              <div className="flex flex-col gap-6">

                {/* 1. Predefined Buttons (Quick Select) */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Bugün', active: false },
                    { label: 'Dün', active: false },
                    { label: 'Bu Ay', active: true },
                    { label: 'Geçen Ay', active: false },
                    { label: 'Bu Yıl', active: false },
                  ].map((btn, i) => (
                    <button
                      key={i}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                        btn.active 
                          ? 'bg-blue-600 text-white shadow-md shadow-blue-200' 
                          : 'bg-slate-50 text-slate-500 hover:bg-slate-100'
                      }`}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>

                <div className="h-px bg-slate-100 w-full" />

                {/* 2. Custom Date Range Inputs */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">Başlangıç</label>
                    <div className="relative group">
                      <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600" />
                      <input 
                        type="date" 
                        defaultValue="2026-04-01"
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-9 pr-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:bg-white focus:border-blue-500 transition-all"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider ml-1">Bitiş</label>
                    <div className="relative group">
                      <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-600" />
                      <input 
                        type="date" 
                        defaultValue="2026-04-30"
                        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 pl-9 pr-3 py-2 text-xs font-semibold text-slate-700 outline-none focus:bg-white focus:border-blue-500 transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* 3. Action Buttons */}
                <div className="flex items-center justify-end gap-3 mt-2">
                  <button className="px-6 py-2 rounded-xl bg-blue-600 text-[11px] font-bold text-white shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95">
                    Uygula
                  </button>
                  <button className="px-4 py-2 rounded-xl border border-slate-200 text-[11px] font-bold text-slate-500 hover:bg-slate-50 transition-all">
                    Vazgeç
                  </button>
                </div>

              </div>
            </div>

          </div>
        </UISection>
      </div>    
    </div>
  );
}
